import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Alert,
  Box,
  Collapse,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import DevicesIcon from "@mui/icons-material/Devices";
import BarChartIcon from "@mui/icons-material/BarChart";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import InsightsIcon from "@mui/icons-material/Insights";
import PeopleIcon from "@mui/icons-material/People";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import RefreshIcon from "@mui/icons-material/Refresh";
import { CartContext } from "../../store/context/CartContext";
import { useAuthStore } from "../../store/zustand/authStore";
import NotAllowed from "../NotAllowed/NotAllowed";
import DeviceSessionsService from "../../services/deviceSessions.service";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatCard = ({ icon, title, value, subtitle, onClick }) => {
  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <CardActionArea onClick={onClick} sx={{ p: 0 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ color: "primary.main" }}>{icon}</Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {title}
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                {value ?? "-"}
              </Typography>
              {subtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const SelectableCard = ({
  icon,
  title,
  subtitle,
  selected,
  onClick,
  right,
}) => {
  return (
    <Card
      elevation={selected ? 6 : 2}
      sx={(theme) => ({
        borderRadius: 3,
        overflow: "hidden",
        border: selected
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        transition: "all .18s ease",
        bgcolor: selected ? "rgba(208, 116, 155, 0.06)" : "background.paper",
      })}
    >
      <CardActionArea onClick={onClick} sx={{ p: 0 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ color: selected ? "primary.main" : "text.secondary" }}>
                {icon}
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={800}>
                  {title}
                </Typography>
                {subtitle ? (
                  <Typography variant="body2" color="text.secondary">
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
            {right ? <Box>{right}</Box> : null}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default function DeviceSessions() {
  const userPermission = useAuthStore((state) => state.user);
  const { setTitleSeccion } = useContext(CartContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [openChart, setOpenChart] = useState("sessionsByType"); // default
  const [days, setDays] = useState(30);
  const chartsRef = useRef(null);

  useEffect(() => {
    setTitleSeccion("Sesiones");
  }, [setTitleSeccion]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await DeviceSessionsService.getStats({ days, limitTopUsers: 10 });
        if (!mounted) return;
        setStats(data);
      } catch (e) {
        if (!mounted) return;
        setError("No se pudieron cargar las estadísticas de sesiones.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [days]);

  const refresh = async (nextDays = days) => {
    try {
      setLoading(true);
      setError("");
      const data = await DeviceSessionsService.getStats({
        days: nextDays,
        limitTopUsers: 10,
      });
      setStats(data);
    } catch {
      setError("No se pudieron cargar las estadísticas de sesiones.");
    } finally {
      setLoading(false);
    }
  };

  const topUsers = useMemo(() => {
    const arr = stats?.topUsers;
    return Array.isArray(arr) ? arr : [];
  }, [stats]);

  const topMobileModels = useMemo(() => {
    const arr = stats?.topMobileModels;
    return Array.isArray(arr) ? arr : [];
  }, [stats]);

  const active = stats?.activeSessions || {};
  const totalsByCategory = stats?.sessionsByCategory || {};

  const inactiveByCategory = {
    web: Math.max((totalsByCategory.web ?? 0) - (active.web ?? 0), 0),
    mobile: Math.max((totalsByCategory.mobile ?? 0) - (active.mobile ?? 0), 0),
    tablet: Math.max((totalsByCategory.tablet ?? 0) - (active.tablet ?? 0), 0),
  };

  const avgDevices = stats?.averageDevicesPerUser || {};
  const mobilePlatformsDevices = stats?.mobilePlatforms?.devices || {};

  const stackedSessionsByCategoryData = useMemo(() => {
    return {
      labels: ["Web", "Mobile", "Tablet"],
      datasets: [
        {
          label: "Activas",
          data: [active.web ?? 0, active.mobile ?? 0, active.tablet ?? 0],
          backgroundColor: "rgba(46, 125, 50, 0.6)", // green
          stack: "stack1",
        },
        {
          label: "Inactivas",
          data: [inactiveByCategory.web, inactiveByCategory.mobile, inactiveByCategory.tablet],
          backgroundColor: "rgba(158, 158, 158, 0.5)", // grey
          stack: "stack1",
        },
      ],
    };
  }, [active.web, active.mobile, active.tablet, inactiveByCategory.web, inactiveByCategory.mobile, inactiveByCategory.tablet]);

  const stackedOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: "top" },
        title: { display: true, text: "Sesiones por tipo (activas vs inactivas)" },
      },
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true },
      },
    }),
    []
  );

  const avgDevicesBarData = useMemo(() => {
    return {
      labels: ["Total", "Web", "Mobile", "Tablet"],
      datasets: [
        {
          label: "Dispositivos promedio por usuaria",
          data: [
            Number(avgDevices.all ?? 0),
            Number(avgDevices.web ?? 0),
            Number(avgDevices.mobile ?? 0),
            Number(avgDevices.tablet ?? 0),
          ],
          backgroundColor: "rgba(53, 162, 235, 0.5)",
        },
      ],
    };
  }, [avgDevices.all, avgDevices.web, avgDevices.mobile, avgDevices.tablet]);

  const avgDevicesOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { display: false },
        title: { display: true, text: "Promedio de dispositivos por usuaria" },
      },
      scales: { y: { beginAtZero: true } },
    }),
    []
  );

  const mobilePlatformsDoughnut = useMemo(() => {
    const android = Number(mobilePlatformsDevices.android ?? 0);
    const ios = Number(mobilePlatformsDevices.ios ?? 0);
    return {
      labels: ["Android", "iOS"],
      datasets: [
        {
          label: "Dispositivos mobile (teléfono)",
          data: [android, ios],
          backgroundColor: ["rgba(255, 159, 64, 0.35)", "rgba(54, 162, 235, 0.35)"],
          borderColor: ["rgba(255, 159, 64, 1)", "rgba(54, 162, 235, 1)"],
          borderWidth: 1,
        },
      ],
    };
  }, [mobilePlatformsDevices.android, mobilePlatformsDevices.ios]);

  const topModelsBar = useMemo(() => {
    const labels = topMobileModels.map((m) => m.model);
    const sessions = topMobileModels.map((m) => Number(m.sessions ?? 0));
    const users = topMobileModels.map((m) => Number(m.users ?? 0));

    return {
      labels,
      datasets: [
        {
          label: "Sesiones",
          data: sessions,
          backgroundColor: "rgba(153, 102, 255, 0.45)",
        },
        {
          label: "Usuarias",
          data: users,
          backgroundColor: "rgba(75, 192, 192, 0.35)",
        },
      ],
    };
  }, [topMobileModels]);

  const topModelsOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: "top" },
        title: { display: true, text: "Top modelos (mobile teléfono) — sesiones vs usuarias" },
      },
      indexAxis: "y",
      scales: { x: { beginAtZero: true } },
    }),
    []
  );

  const toggle = (key) => {
    setOpenChart((prev) => (prev === key ? null : key));
  };

  const openChartFromCard = (key) => {
    setOpenChart(key);
    // Scroll suave al panel de gráficos
    setTimeout(() => {
      chartsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  return (
    <>
      <NotAllowed user={userPermission} page={"USERS"} />

      <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <QueryStatsIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Estadísticas de sesiones
          </Typography>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !stats ? (
          <Alert severity="info">Sin datos.</Alert>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 2,
                mb: 2,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.paper",
              })}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    Resumen
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Período: últimos <strong>{days}</strong> días · Último acceso:{" "}
                    {formatDateTime(stats.lastAccessAt)}
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                  <ToggleButtonGroup
                    value={days}
                    exclusive
                    onChange={(_, v) => {
                      if (!v) return;
                      setDays(v);
                    }}
                    size="small"
                  >
                    <ToggleButton value={7}>7d</ToggleButton>
                    <ToggleButton value={30}>30d</ToggleButton>
                    <ToggleButton value={90}>90d</ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => refresh(days)}
                    disabled={loading}
                    size="small"
                  >
                    Refrescar
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`Activas: ${active.total ?? 0}`} color="success" variant="outlined" />
                  <Chip label={`Web: ${active.web ?? 0}`} variant="outlined" />
                  <Chip label={`Mobile: ${active.mobile ?? 0}`} variant="outlined" />
                  <Chip label={`Tablet: ${active.tablet ?? 0}`} variant="outlined" />
                </Stack>
              </Stack>
            </Paper>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<DevicesIcon />}
                  title="Sesiones activas (total)"
                  value={active.total ?? 0}
                  subtitle="En el período seleccionado"
                  onClick={() => openChartFromCard("sessionsByType")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<DevicesIcon />}
                  title="Activas por tipo"
                  value={`Web ${active.web ?? 0}`}
                  subtitle={`Mobile ${active.mobile ?? 0} · Tablet ${active.tablet ?? 0}`}
                  onClick={() => openChartFromCard("sessionsByType")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<PeopleIcon />}
                  title="Usuarias con sesiones"
                  value={stats.usersWithSessions}
                  subtitle={`Sesiones totales (período): ${stats.totalSessions ?? 0}`}
                  onClick={() => {
                    setTimeout(() => {
                      document
                        .getElementById("device-sessions-top-users")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 0);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<DevicesIcon />}
                  title="Promedio dispositivos/usuaria"
                  value={Number(avgDevices.all ?? 0).toFixed(2)}
                  subtitle={`Web ${Number(avgDevices.web ?? 0).toFixed(2)} · Mobile ${Number(
                    avgDevices.mobile ?? 0
                  ).toFixed(2)} · Tablet ${Number(avgDevices.tablet ?? 0).toFixed(2)}`}
                  onClick={() => openChartFromCard("avgDevices")}
                />
              </Grid>
            </Grid>

            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 2,
                mb: 2,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.paper",
              })}
              ref={chartsRef}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <InsightsIcon color="primary" />
                <Typography variant="h6" fontWeight={800}>
                  Gráficos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  (tocá una card para desplegar)
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SelectableCard
                    icon={<BarChartIcon />}
                    title="Sesiones por tipo"
                    subtitle="Activas vs inactivas (web/mobile/tablet)"
                    selected={openChart === "sessionsByType"}
                    onClick={() => toggle("sessionsByType")}
                    right={<Chip label={`Activas: ${active.total ?? 0}`} size="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SelectableCard
                    icon={<DonutLargeIcon />}
                    title="Mobile: Android vs iOS"
                    subtitle="Dispositivos (teléfono) por OS"
                    selected={openChart === "mobileOs"}
                    onClick={() => toggle("mobileOs")}
                    right={
                      <Stack direction="row" spacing={1}>
                        <Chip label={`Android ${mobilePlatformsDevices.android ?? 0}`} size="small" />
                        <Chip label={`iOS ${mobilePlatformsDevices.ios ?? 0}`} size="small" />
                      </Stack>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SelectableCard
                    icon={<BarChartIcon />}
                    title="Promedio de dispositivos"
                    subtitle="Promedio por usuaria (total/web/mobile/tablet)"
                    selected={openChart === "avgDevices"}
                    onClick={() => toggle("avgDevices")}
                    right={<Chip label={`${Number(avgDevices.all ?? 0).toFixed(2)}`} size="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SelectableCard
                    icon={<BarChartIcon />}
                    title="Top modelos mobile"
                    subtitle="Sesiones vs usuarias por modelo (teléfono)"
                    selected={openChart === "topModels"}
                    onClick={() => toggle("topModels")}
                    right={<Chip label={`Top ${topMobileModels.length}`} size="small" />}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Collapse in={openChart === "sessionsByType"} unmountOnExit>
                  <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
                    <Bar options={stackedOptions} data={stackedSessionsByCategoryData} />
                  </Card>
                </Collapse>

                <Collapse in={openChart === "mobileOs"} unmountOnExit>
                  <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <Doughnut data={mobilePlatformsDoughnut} />
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                          Dispositivos mobile (teléfono)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Android: {mobilePlatformsDevices.android ?? 0} · iOS: {mobilePlatformsDevices.ios ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Tip: tablet se mide aparte (formFactor=tablet).
                        </Typography>
                      </Grid>
                    </Grid>
                  </Card>
                </Collapse>

                <Collapse in={openChart === "avgDevices"} unmountOnExit>
                  <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
                    <Bar options={avgDevicesOptions} data={avgDevicesBarData} />
                  </Card>
                </Collapse>

                <Collapse in={openChart === "topModels"} unmountOnExit>
                  <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
                    {topMobileModels.length === 0 ? (
                      <Alert severity="info">Aún no hay modelos mobile suficientes para graficar.</Alert>
                    ) : (
                      <Bar options={topModelsOptions} data={topModelsBar} />
                    )}
                  </Card>
                </Collapse>
              </Box>
            </Paper>

            <Paper elevation={1} sx={{ p: 2 }} id="device-sessions-top-users">
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  Top usuarias por cantidad de sesiones
                </Typography>
                <Chip label={`Top ${topUsers.length}`} />
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {topUsers.length === 0 ? (
                <Alert severity="info">No hay registros suficientes para mostrar.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>User ID</strong></TableCell>
                        <TableCell><strong>Nombre</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell align="right"><strong>Sesiones</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topUsers.map((row) => (
                        <TableRow key={row.userId}>
                          <TableCell>{row.userId}</TableCell>
                          <TableCell>
                            {row.user ? `${row.user.name ?? ""} ${row.user.lastname ?? ""}`.trim() : "-"}
                          </TableCell>
                          <TableCell>{row.user?.email ?? "-"}</TableCell>
                          <TableCell align="right">{row.sessions}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  Top modelos (mobile teléfono)
                </Typography>
                <Chip label={`Top ${topMobileModels.length}`} />
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {topMobileModels.length === 0 ? (
                <Alert severity="info">No hay registros suficientes para mostrar.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Modelo</strong></TableCell>
                        <TableCell align="right"><strong>Sesiones</strong></TableCell>
                        <TableCell align="right"><strong>Sesiones activas</strong></TableCell>
                        <TableCell align="right"><strong>Usuarias</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topMobileModels.map((m) => (
                        <TableRow key={m.model}>
                          <TableCell>{m.model}</TableCell>
                          <TableCell align="right">{m.sessions}</TableCell>
                          <TableCell align="right">{m.activeSessions}</TableCell>
                          <TableCell align="right">{m.users}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Box>
    </>
  );
}


